import { useEffect, useState } from "react"

// Detecta si el navegador corre en un celular (UA + viewport).
// Nota: tablets en landscape grande caen como desktop. La intención es
// distinguir "pantalla pequeña con dedos" de "pantalla grande con cursor".
function compute(): boolean {
    if (typeof window === "undefined") return false
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || ""
    const uaMatch = /iPhone|iPod|(Android[\s\S]*?Mobile)/i.test(ua)
    const small =
        (window.innerWidth || 1024) <= 768 ||
        (window.innerHeight || 1024) <= 600
    return uaMatch || small
}

export function useIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState<boolean>(compute)
    useEffect(() => {
        const update = () => setIsMobile(compute())
        update()
        window.addEventListener("resize", update)
        window.addEventListener("orientationchange", update)
        return () => {
            window.removeEventListener("resize", update)
            window.removeEventListener("orientationchange", update)
        }
    }, [])
    return isMobile
}

export type Orientation = "portrait" | "landscape"

function computeOrient(): Orientation {
    if (typeof window === "undefined") return "portrait"
    try {
        const t = (window.screen as any)?.orientation?.type as string | undefined
        if (typeof t === "string") {
            if (t.startsWith("landscape")) return "landscape"
            if (t.startsWith("portrait")) return "portrait"
        }
    } catch {}
    try {
        if (window.matchMedia("(orientation: landscape)").matches)
            return "landscape"
    } catch {}
    return (window.innerWidth || 0) > (window.innerHeight || 0)
        ? "landscape"
        : "portrait"
}

export function useOrientation(): Orientation {
    const [orient, setOrient] = useState<Orientation>(computeOrient)
    useEffect(() => {
        const update = () => setOrient(computeOrient())
        update()
        window.addEventListener("resize", update)
        window.addEventListener("orientationchange", update)
        let mq: MediaQueryList | null = null
        try {
            mq = window.matchMedia("(orientation: landscape)")
            mq.addEventListener("change", update)
        } catch {}
        let scrOrient: any = null
        try {
            scrOrient = (window.screen as any)?.orientation
            scrOrient?.addEventListener?.("change", update)
        } catch {}
        return () => {
            window.removeEventListener("resize", update)
            window.removeEventListener("orientationchange", update)
            try {
                mq?.removeEventListener?.("change", update)
            } catch {}
            try {
                scrOrient?.removeEventListener?.("change", update)
            } catch {}
        }
    }, [])
    return orient
}

// Best-effort: pide fullscreen del root + lock orientation a landscape.
// Cualquier rama puede fallar; el caller debe poder seguir si vuelve false.
export async function lockLandscape(): Promise<boolean> {
    if (typeof document === "undefined") return false
    const orient: any = (window.screen as any)?.orientation
    const supports = typeof orient?.lock === "function"
    if (!supports) return false
    const el: any = document.documentElement
    try {
        const reqFs =
            el.requestFullscreen ||
            el.webkitRequestFullscreen ||
            el.mozRequestFullScreen ||
            el.msRequestFullscreen
        if (reqFs && !document.fullscreenElement) await reqFs.call(el)
    } catch {}
    try {
        await orient.lock("landscape")
        return true
    } catch {
        return false
    }
}

export function supportsOrientationLock(): boolean {
    if (typeof window === "undefined") return false
    const orient: any = (window.screen as any)?.orientation
    return typeof orient?.lock === "function"
}
